import { useState, useMemo } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  KeyboardSensor,
  useSensor,
  useSensors,
  closestCorners,
  useDroppable,
  type DragStartEvent,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useReorderTasks, useProjects } from '@/hooks/useTasks.ts';
import { formatDue, isOverdue } from '@/lib/dates.ts';
import { planMove } from '@/lib/ordering.ts';
import { STATUS_LABELS } from '@/lib/grouping.ts';
import { Tag } from './ui.tsx';
import type { ReorderMove } from '@/lib/api.ts';
import type { TaskWithRelations, TaskStatus } from '@shared/types';

const STATUS_COLUMNS: TaskStatus[] = ['inbox', 'todo', 'in_progress', 'blocked', 'done'];
const COLUMN_PREFIX = 'col:';
const RAIL_HEIGHT: Record<number, string> = { 0: '0px', 1: '8px', 2: '14px', 3: '20px' };

export type BoardGrouping = 'status' | 'project_id';

interface BoardColumn {
  key: string;
  label: string;
  tasks: TaskWithRelations[];
}

interface Props {
  tasks: TaskWithRelations[];
  grouping: BoardGrouping;
  onOpen: (id: string) => void;
}

export function Board({ tasks, grouping, onOpen }: Props) {
  const { data: projects } = useProjects();
  const reorder = useReorderTasks();
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const columns = useMemo(
    () => buildColumns(tasks, grouping, projects ?? []),
    [tasks, grouping, projects],
  );

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 180, tolerance: 6 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  function handleDragEnd(event: DragEndEvent) {
    setDraggingId(null);
    const { active, over } = event;
    if (!over) return;

    const activeId = String(active.id);
    const overId = String(over.id);
    if (activeId === overId) return;

    const from = columns.find((c) => c.tasks.some((t) => t.id === activeId));
    if (!from) return;

    const to = overId.startsWith(COLUMN_PREFIX)
      ? columns.find((c) => c.key === overId.slice(COLUMN_PREFIX.length))
      : columns.find((c) => c.tasks.some((t) => t.id === overId));
    if (!to) return;

    const withoutActive = to.tasks.filter((t) => t.id !== activeId);

    let index: number;
    if (overId.startsWith(COLUMN_PREFIX)) {
      index = withoutActive.length;
    } else {
      const overIndex = withoutActive.findIndex((t) => t.id === overId);
      if (overIndex === -1) return;
      const activeIndex = from.tasks.findIndex((t) => t.id === activeId);
      const overOriginalIndex = from.tasks.findIndex((t) => t.id === overId);
      const movingDown =
        from.key === to.key && activeIndex !== -1 && activeIndex < overOriginalIndex;
      index = movingDown ? overIndex + 1 : overIndex;
    }

    const moves: ReorderMove[] = planMove(activeId, withoutActive, index);

    if (from.key !== to.key) {
      const move = moves.find((m) => m.id === activeId);
      if (move) {
        if (grouping === 'status') move.status = to.key as TaskStatus;
        else move.project_id = to.key === 'none' ? null : to.key;
      }
    }

    reorder.mutate(moves);
  }

  const draggingTask = draggingId ? tasks.find((t) => t.id === draggingId) : null;

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={closestCorners}
      onDragStart={(e: DragStartEvent) => setDraggingId(String(e.active.id))}
      onDragEnd={handleDragEnd}
      onDragCancel={() => setDraggingId(null)}
    >
      <div className="-mx-5 flex gap-4 overflow-x-auto px-5 pb-4 sm:mx-0 sm:px-0">
        {columns.map((column) => (
          <Column key={column.key} column={column} onOpen={onOpen} dragging={draggingId !== null} />
        ))}
      </div>

      {/* The only card that ever floats is the one in your hand, which is what
          makes the shadow mean something. */}
      <DragOverlay dropAnimation={{ duration: 160, easing: 'cubic-bezier(.2,.8,.2,1)' }}>
        {draggingTask ? (
          <div className="w-72 rotate-1 rounded-sm border border-brass bg-overlay shadow-2xl shadow-black/60">
            <CardBody task={draggingTask} />
          </div>
        ) : null}
      </DragOverlay>
    </DndContext>
  );
}

/**
 * Columns are bounded ledger columns, not floating card decks: a raised
 * surface, a header rule, and cards separated by hairlines rather than gaps.
 * Trello-style floating cards were the obvious move and the wrong one — five
 * columns of shadowed rectangles is visual noise, and it gave the app no
 * boundary clarity at all.
 */
function Column({
  column,
  onOpen,
  dragging,
}: {
  column: BoardColumn;
  onOpen: (id: string) => void;
  dragging: boolean;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `${COLUMN_PREFIX}${column.key}` });

  return (
    <section
      className={`flex w-72 shrink-0 flex-col rounded-md border bg-raised transition-colors duration-[120ms] ease-out ${
        isOver ? 'border-brass' : 'border-line'
      }`}
    >
      <header className="flex items-baseline justify-between border-b border-line px-3 py-2.5">
        <h2 className="t-label">{column.label}</h2>
        <span className="t-meta text-ink-3" data-numeric>
          {column.tasks.length}
        </span>
      </header>

      <div ref={setNodeRef} className="flex min-h-32 flex-1 flex-col">
        <SortableContext items={column.tasks.map((t) => t.id)} strategy={verticalListSortingStrategy}>
          {column.tasks.map((task) => (
            <SortableCard key={task.id} task={task} onOpen={onOpen} />
          ))}
        </SortableContext>

        {column.tasks.length === 0 && (
          <div
            className={`m-2 flex flex-1 items-center justify-center rounded-sm border border-dashed px-3 py-6 text-center transition-colors duration-[120ms] ease-out ${
              isOver
                ? 'border-brass bg-brass/10 text-brass'
                : dragging
                  ? 'border-line-strong text-ink-3'
                  : 'border-line text-ink-3'
            }`}
          >
            <span className="t-meta">{isOver ? 'Release to move here' : 'Drag a task here'}</span>
          </div>
        )}
      </div>
    </section>
  );
}

function SortableCard({ task, onOpen }: { task: TaskWithRelations; onOpen: (id: string) => void }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: task.id,
  });

  return (
    <div
      ref={setNodeRef}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      className={`group relative cursor-grab border-b border-line transition-colors duration-[120ms] ease-out last:border-b-0 hover:bg-hover active:cursor-grabbing ${
        isDragging ? 'opacity-30' : ''
      }`}
      onClick={() => onOpen(task.id)}
      {...attributes}
      {...listeners}
    >
      <CardBody task={task} />
    </div>
  );
}

function CardBody({ task }: { task: TaskWithRelations }) {
  const closed = task.status === 'done' || task.status === 'cancelled';
  const overdue = !closed && isOverdue(task.due_at);

  return (
    <article className="relative px-3 py-2.5">
      <span
        aria-hidden="true"
        className="rail"
        style={{
          height: closed ? '0px' : RAIL_HEIGHT[task.priority],
          background: overdue ? 'var(--color-brick)' : 'var(--color-brass)',
        }}
      />
      <p className={`text-sm leading-snug ${closed ? 'text-ink-3 line-through' : 'text-ink'}`}>
        {task.title}
      </p>

      {(task.due_at || task.tags.length > 0 || task.subtask_count > 0) && (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
          {task.due_at && (
            <time dateTime={task.due_at} className={`t-meta ${overdue ? 'text-brick' : 'text-ink-2'}`}>
              {formatDue(task.due_at)}
            </time>
          )}
          {task.subtask_count > 0 && (
            <Tag>
              <span data-numeric>
                {task.subtask_done_count}/{task.subtask_count}
              </span>
            </Tag>
          )}
          {task.tags.map((tag) => (
            <Tag key={tag.id}>#{tag.name}</Tag>
          ))}
        </div>
      )}
    </article>
  );
}

function buildColumns(
  tasks: TaskWithRelations[],
  grouping: BoardGrouping,
  projects: { id: string; name: string }[],
): BoardColumn[] {
  const byPosition = (a: TaskWithRelations, b: TaskWithRelations) =>
    a.position - b.position || a.created_at.localeCompare(b.created_at);

  if (grouping === 'status') {
    return STATUS_COLUMNS.map((status) => ({
      key: status,
      label: STATUS_LABELS[status],
      tasks: tasks.filter((t) => t.status === status).sort(byPosition),
    }));
  }

  const columns: BoardColumn[] = projects.map((project) => ({
    key: project.id,
    label: project.name,
    tasks: tasks.filter((t) => t.project_id === project.id).sort(byPosition),
  }));

  columns.push({
    key: 'none',
    label: 'No project',
    tasks: tasks.filter((t) => t.project_id === null).sort(byPosition),
  });

  return columns;
}
